import React, { useState, useEffect, useMemo } from "react";
import {
  createFeeHead,
  updateFeeHead,
  createFeeHeadPortal,
  updateFeeHeadPortal,
} from "../../api/fees/feeApi";
import {
  Button,
  Input,
  Select,
  Textarea,
  Checkbox,
  Modal,
} from "../../components/ui";
import { useToast } from "../../context/ToastContext";

const FEE_TYPES = [
  "RECURRING_MONTHLY",
  "ONE_TIME",
  "VARIABLE",
  "FINE",
  "DISCOUNT",
  "TRANSPORT",
];

const FREQUENCIES = ["MONTHLY", "QUARTERLY", "ANNUAL", "ONE_TIME"];

const SOURCES = [
  { value: "MANUAL", label: "Manual (amount set per class in Fee Structure)" },
  { value: "TRANSPORT_MODULE", label: "Transport module (by student)" },
];

const emptyForm = {
  name: "",
  code: "",
  type: "RECURRING_MONTHLY",
  description: "",
  frequency: "MONTHLY",
  source: "MANUAL",
  isActive: true,
};

const FeeHeadModal = ({
  isOpen,
  onClose,
  onSuccess,
  editData = null,
  isPortal = false,
}) => {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (!isOpen) return;
    if (editData) {
      setForm({
        name: editData.name || "",
        code: editData.code || "",
        type: editData.type || "RECURRING_MONTHLY",
        description: editData.description || "",
        frequency: editData.frequency || "MONTHLY",
        source:
          editData.source ||
          (editData.type === "TRANSPORT" ? "TRANSPORT_MODULE" : "MANUAL"),
        isActive: editData.isActive !== undefined ? editData.isActive : true,
      });
    } else {
      setForm(emptyForm);
    }
  }, [editData, isOpen]);

  const feeTypeOptions = useMemo(
    () => FEE_TYPES.map((t) => ({ label: t, value: t })),
    []
  );

  const frequencyOptions = useMemo(
    () => FREQUENCIES.map((f) => ({ label: f, value: f })),
    []
  );

  const sourceOptions = useMemo(() => SOURCES, []);

  const handleTypeChange = (typeValue) => {
    setForm((prev) => ({
      ...prev,
      type: typeValue,
      source: typeValue === "TRANSPORT" ? "TRANSPORT_MODULE" : prev.source,
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!editData && (!form.name.trim() || !form.code.trim() || !form.type)) {
      toast.error("Name, Code and Type are required.");
      return;
    }
    setLoading(true);
    try {
      if (editData?._id) {
        const payload = {
          description: form.description,
          type: form.type,
          isActive: form.isActive,
          frequency: form.frequency,
          source: form.type === "TRANSPORT" ? "TRANSPORT_MODULE" : form.source,
        };
        if (isPortal) await updateFeeHeadPortal(editData._id, payload);
        else await updateFeeHead(editData._id, payload);
      } else {
        const payload = {
          name: form.name.trim(),
          code: form.code.trim(),
          type: form.type,
          description: form.description || "",
          frequency: form.frequency,
          source: form.type === "TRANSPORT" ? "TRANSPORT_MODULE" : form.source,
        };
        if (isPortal) await createFeeHeadPortal(payload);
        else await createFeeHead(payload);
      }
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editData ? "Edit Fee Head" : "Create Fee Head"}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} isLoading={loading}>
            {editData ? "Update" : "Create"}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        

        {!editData && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Name *"
              name="name"
              value={form.name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder="e.g. Tuition Fee"
              required
            />
            <Input
              label="Code *"
              name="code"
              value={form.code}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  code: e.target.value.toUpperCase(),
                }))
              }
              placeholder="e.g. TUITION"
              required
            />
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label="Type *"
            options={feeTypeOptions}
            value={form.type}
            onChange={handleTypeChange}
          />
          <Select
            label="Frequency"
            options={frequencyOptions}
            value={form.frequency}
            onChange={(val) =>
              setForm((prev) => ({ ...prev, frequency: val }))
            }
          />
        </div>

        <div>
          <Select
            label="Amount Source"
            options={sourceOptions}
            value={form.source}
            disabled={form.type === "TRANSPORT"}
            onChange={(val) => setForm((prev) => ({ ...prev, source: val }))}
          />
          {(form.type === "TRANSPORT" || form.source === "TRANSPORT_MODULE") && (
            <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
              Transport amount comes from Transport module by student (later).
            </p>
          )}
          {form.source === "MANUAL" && form.type !== "TRANSPORT" && (
            <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
              After saving, go to Fee Structure to set the amount for each class.
            </p>
          )}
        </div>

        <Textarea
          label="Description"
          name="description"
          value={form.description}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, description: e.target.value }))
          }
          placeholder="Optional fee description or notes"
          rows={2}
        />

        {editData && (
          <Checkbox
            label="Active"
            checked={form.isActive}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, isActive: e.target.checked }))
            }
          />
        )}
      </form>
    </Modal>
  );
};

export default FeeHeadModal;